import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import EditableTitle from '@/components/ui/EditableTitle';
import { Input } from '@/components/ui/input';
import { ListaReordenable } from '@/components/ui/ListaReordenable';
import NumberInput from '@/components/ui/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { armarBonificacion } from '@/components/Costos/calculo';
import { SelectorDestinoPago } from '@/components/Costos/SelectorDestinoPago';
import type { AdicionalCalculo, TipoBonificacion, TipoItem, TipoMoneda } from '@/types/costo';
import { ETIQUETA_CAJA, PARTES } from '../constants/presupuestos';
import { PRESUPUESTOS_ROUTES, rutaDetallePlantilla } from '../constants/routes';
import { useDestinosPagoOpciones } from '../hooks/useDestinosPago';
import { usePlantilla } from '../hooks/usePlantillas';
import { usePresupuestosProformaPermissions } from '../hooks/usePresupuestosProformaPermissions';
import type {
  BaseCalculo,
  DestinoPagoOpcion,
  ParteItem,
  PlantillaItem,
  PlantillaItemFormData,
} from '../types';

/**
 * Edición de una plantilla: sus ítems en dos cajas, una por parte. La parte la
 * determina la caja; no hay selector.
 *
 * Cada ítem puede ser un monto fijo o un porcentaje de la base imponible o del
 * monto de venta más una suma fija (en ese caso `monto` es la suma fija, con su
 * propia moneda), en cualquier moneda. Al aplicar la plantilla, una base o una
 * suma fija en otra moneda que el ítem se convierte con la cotización del dólar
 * del presupuesto.
 */
export const PlantillaDetallePage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permisos = usePresupuestosProformaPermissions();
  const {
    plantilla,
    isLoading,
    actualizarPlantilla,
    crearItem,
    actualizarItem,
    eliminarItem,
    reordenarItems,
    duplicarPlantilla,
    isGuardando,
  } = usePlantilla(id);
  const { opciones: destinosPago } = useDestinosPagoOpciones({ enabled: permisos.canManageBases });

  const [nuevos, setNuevos] = useState<Record<ParteItem, string>>({
    adquiriente: '',
    transferente: '',
  });

  if (!permisos.canManageBases) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        No tenés permisos para administrar las plantillas de presupuesto.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!plantilla) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        No se encontró la plantilla solicitada.
      </div>
    );
  }

  const guardarItem = async (itemId: string, data: PlantillaItemFormData) => {
    try {
      await actualizarItem({ itemId, data });
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'No se pudo guardar el ítem.');
    }
  };

  const handleCrear = async (parte: ParteItem) => {
    const nombre = nuevos[parte].trim();

    if (!nombre) {
      toast.error('El nombre del ítem es obligatorio');
      return;
    }

    try {
      await crearItem({ parte, nombre, moneda: 'ARS', tipo: 'egreso', modo_calculo: 'fijo' });
      setNuevos((actual) => ({ ...actual, [parte]: '' }));
    } catch {
      toast.error('No se pudo crear el ítem.');
    }
  };

  const reordenar = async (ids: string[]) => {
    try {
      await reordenarItems(ids);
    } catch {
      toast.error('No se pudieron reordenar los ítems.');
    }
  };

  const handleDuplicar = async () => {
    try {
      const copia = await duplicarPlantilla();
      toast.success(`Se creó "${copia.nombre}".`);
      navigate(rutaDetallePlantilla(copia.id));
    } catch {
      toast.error('No se pudo duplicar la plantilla.');
    }
  };

  const handleRenombrar = async (nombre: string) => {
    if (!nombre) return;

    try {
      await actualizarPlantilla({ nombre });
    } catch {
      toast.error('No se pudo renombrar la plantilla.');
    }
  };

  return (
    <div className="space-y-6 p-2 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={PRESUPUESTOS_ROUTES.PLANTILLAS} aria-label="Volver a plantillas">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <EditableTitle
            title={plantilla.nombre}
            onSave={handleRenombrar}
            className="text-2xl font-bold"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            title={plantilla.activo ? 'Desactivar' : 'Activar'}
            onClick={() => void actualizarPlantilla({ activo: !plantilla.activo })}
            disabled={isGuardando}
          >
            <Badge variant={plantilla.activo ? 'default' : 'secondary'}>
              {plantilla.activo ? 'Activa' : 'Inactiva'}
            </Badge>
          </Button>
          <Button variant="outline" onClick={() => void handleDuplicar()} disabled={isGuardando}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicar
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Solo los ítems activos se copian al presupuesto. Los ítems “% de la base imponible” se
        calculan con la base imponible del presupuesto y se recalculan cuando cambia.
      </p>

      <div className="grid gap-6 xl:grid-cols-2">
        {PARTES.map((parte) => {
          const items = plantilla.items[parte] ?? [];

          return (
            <Card key={parte} data-testid={`plantilla-${parte}`}>
              <CardHeader>
                <CardTitle className="text-base">{ETIQUETA_CAJA[parte]}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={nuevos[parte]}
                    onChange={(event) =>
                      setNuevos((actual) => ({ ...actual, [parte]: event.target.value }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') void handleCrear(parte);
                    }}
                    placeholder="Nombre del ítem"
                    aria-label={`Nuevo ítem de ${parte}`}
                    maxLength={255}
                    disabled={isGuardando}
                  />
                  <Button onClick={() => void handleCrear(parte)} disabled={isGuardando}>
                    <Plus className="mr-1 h-4 w-4" />
                    Agregar
                  </Button>
                </div>

                {items.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Sin ítems todavía
                  </p>
                ) : (
                  <ListaReordenable
                    as="ul"
                    items={items}
                    onReordenar={(ids) => void reordenar(ids)}
                    etiquetaItem={(item) => item.nombre}
                    className="space-y-2"
                  >
                    {(item, asa) => (
                      <FilaItemPlantilla
                        item={item}
                        asa={asa}
                        isGuardando={isGuardando}
                        destinosPago={destinosPago}
                        onGuardar={(data) => guardarItem(item.id, data)}
                        onEliminar={() => void eliminarItem(item.id)}
                      />
                    )}
                  </ListaReordenable>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

interface FilaItemPlantillaProps {
  item: PlantillaItem;
  /** Manija para arrastrar la fila y cambiar el orden. */
  asa: React.ReactNode;
  isGuardando: boolean;
  destinosPago: DestinoPagoOpcion[];
  onGuardar: (data: PlantillaItemFormData) => Promise<void>;
  onEliminar: () => void;
}

const FilaItemPlantilla: React.FC<FilaItemPlantillaProps> = ({
  item,
  asa,
  isGuardando,
  destinosPago,
  onGuardar,
  onEliminar,
}) => {
  const esPorcentaje = item.modoCalculo === 'porcentaje';
  // Pasar a porcentaje adicional se guarda recién cuando se carga el valor.
  const [adicional, setAdicional] = useState<AdicionalCalculo>(
    item.porcentajeAdicional != null ? 'porcentaje' : 'suma_fija'
  );
  const conPorcentajeAdicional = esPorcentaje && adicional === 'porcentaje';
  const conSumaFija = esPorcentaje && !conPorcentajeAdicional;

  useEffect(() => {
    setAdicional(item.porcentajeAdicional != null ? 'porcentaje' : 'suma_fija');
  }, [item.porcentajeAdicional]);

  // Elegir un tipo de bonificación se guarda recién cuando se carga el valor.
  const [tipoBonificacion, setTipoBonificacion] = useState<TipoBonificacion | 'ninguna'>(
    item.bonificacionTipo ?? 'ninguna'
  );

  useEffect(() => {
    setTipoBonificacion(item.bonificacionTipo ?? 'ninguna');
  }, [item.bonificacionTipo]);

  const quitarBonificacion = () => {
    if (item.bonificacionTipo) void onGuardar({ bonificacion_tipo: null, bonificacion_valor: null });
  };

  const guardarBonificacion = (tipo: TipoBonificacion, valor: number | null) => {
    if (valor === null) {
      quitarBonificacion();
      return;
    }

    const resultado = armarBonificacion(tipo, valor);
    if ('error' in resultado) {
      toast.error(resultado.error);
      return;
    }

    if (tipo !== item.bonificacionTipo || valor !== item.bonificacionValor) {
      void onGuardar(resultado.datos);
    }
  };

  return (
    <div
      className={`space-y-2 rounded-lg border bg-card p-2 ${item.activo ? '' : 'opacity-60'}`}
      data-testid={`item-plantilla-${item.id}`}
    >
      <div className="flex items-center gap-2">
        {asa}
        <Input
          defaultValue={item.nombre}
          aria-label={`Editar nombre de ${item.nombre}`}
          className="h-8 flex-1"
          maxLength={255}
          onBlur={(event) => {
            const nombre = event.target.value.trim();
            if (nombre && nombre !== item.nombre) void onGuardar({ nombre });
          }}
        />

        <Button
          size="sm"
          variant="ghost"
          title={item.activo ? 'Desactivar' : 'Activar'}
          onClick={() => void onGuardar({ activo: !item.activo })}
        >
          <Badge variant={item.activo ? 'default' : 'secondary'}>
            {item.activo ? 'Activo' : 'Inactivo'}
          </Badge>
        </Button>
        <Button
          size="icon"
          variant="ghost"
          title="Eliminar ítem"
          aria-label={`Eliminar ${item.nombre}`}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onEliminar}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Select
          value={esPorcentaje ? item.baseCalculo : 'fijo'}
          onValueChange={(valor: 'fijo' | BaseCalculo) =>
            void onGuardar(
              valor === 'fijo'
                ? { modo_calculo: 'fijo' }
                : { modo_calculo: 'porcentaje', base_calculo: valor }
            )
          }
        >
          <SelectTrigger size="sm" className="h-8 w-48" aria-label={`Cálculo de ${item.nombre}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fijo">Monto fijo</SelectItem>
            <SelectItem value="base_imponible">% de la base imponible</SelectItem>
            <SelectItem value="monto_venta">% del monto de venta</SelectItem>
          </SelectContent>
        </Select>

        {esPorcentaje && (
          <>
            <NumberInput
              key={`porcentaje-${item.porcentaje}`}
              aria-label={`Porcentaje de ${item.nombre}`}
              decimals={4}
              min={0}
              max={100}
              defaultValue={item.porcentaje ?? ''}
              onAccept={(valor) => {
                if (valor !== null && valor !== item.porcentaje) void onGuardar({ porcentaje: valor });
              }}
              containerClassName="w-24"
              className="h-8"
              placeholder="%"
            />
            <span className="text-muted-foreground">%</span>
            <Select
              value={adicional}
              onValueChange={(valor: AdicionalCalculo) => {
                setAdicional(valor);
                if (valor === 'suma_fija' && item.porcentajeAdicional != null) {
                  void onGuardar({ porcentaje_adicional: null });
                }
              }}
            >
              <SelectTrigger
                size="sm"
                className="h-8 w-36"
                aria-label={`Qué aplicar al resultado de ${item.nombre}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="suma_fija">+ suma fija</SelectItem>
                <SelectItem value="porcentaje">× porcentaje</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}

        {conPorcentajeAdicional ? (
          <>
            <NumberInput
              key={`porcentaje-adicional-${item.porcentajeAdicional}`}
              aria-label={`Porcentaje sobre el resultado de ${item.nombre}`}
              decimals={4}
              min={0}
              max={100}
              defaultValue={item.porcentajeAdicional ?? ''}
              onAccept={(valor) => {
                if (valor !== null && valor !== item.porcentajeAdicional) {
                  void onGuardar({ porcentaje_adicional: valor });
                }
              }}
              containerClassName="w-24"
              className="h-8"
              placeholder="Ej: 21"
            />
            <span className="text-muted-foreground">%</span>
          </>
        ) : (
          <NumberInput
            key={`monto-${item.monto}`}
            aria-label={esPorcentaje ? `Suma fija de ${item.nombre}` : `Monto de ${item.nombre}`}
            currency
            decimals={2}
            min={0}
            defaultValue={item.monto}
            onAccept={(valor) => {
              const monto = valor ?? 0;
              if (monto !== item.monto) void onGuardar({ monto });
            }}
            containerClassName="w-36"
            className="h-8"
            placeholder={esPorcentaje ? 'Suma fija' : 'Monto'}
          />
        )}

        {conSumaFija && (
          <>
            <Select
              value={item.sumaFijaMoneda ?? item.moneda}
              onValueChange={(moneda: TipoMoneda) => void onGuardar({ suma_fija_moneda: moneda })}
            >
              <SelectTrigger
                size="sm"
                className="h-8 w-20"
                aria-label={`Moneda de la suma fija de ${item.nombre}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ARS">ARS</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-muted-foreground">Ítem en</span>
          </>
        )}

        <Select
          value={item.moneda}
          onValueChange={(moneda: TipoMoneda) => void onGuardar({ moneda })}
        >
          <SelectTrigger size="sm" className="h-8 w-20" aria-label={`Moneda de ${item.nombre}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ARS">ARS</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>

        {/* En el PDF, una moneda distinta de la del ítem se convierte con la cotización. */}
        <span className="text-muted-foreground">imprime en</span>
        <Select
          value={item.monedaImpresion ?? item.moneda}
          onValueChange={(moneda: TipoMoneda) =>
            void onGuardar({ moneda_impresion: moneda === item.moneda ? null : moneda })
          }
        >
          <SelectTrigger size="sm" className="h-8 w-20" aria-label={`Moneda de impresión de ${item.nombre}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ARS">ARS</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
          </SelectContent>
        </Select>

        <SelectorDestinoPago
          value={item.destinoPagoId}
          opciones={destinosPago}
          onChange={(destino_pago_id) => void onGuardar({ destino_pago_id })}
          ariaLabel={`Destino de pago de ${item.nombre}`}
          disabled={isGuardando}
        />

        <Select value={item.tipo} onValueChange={(tipo: TipoItem) => void onGuardar({ tipo })}>
          <SelectTrigger
            size="sm"
            className={`h-8 w-28 ${
              item.tipo === 'ingreso' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}
            aria-label={`Tipo de ${item.nombre}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ingreso">Ingreso</SelectItem>
            <SelectItem value="egreso">Egreso</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={tipoBonificacion}
          onValueChange={(tipo: TipoBonificacion | 'ninguna') => {
            setTipoBonificacion(tipo);
            if (tipo === 'ninguna') {
              quitarBonificacion();
            } else if (item.bonificacionValor != null) {
              guardarBonificacion(tipo, item.bonificacionValor);
            }
          }}
        >
          <SelectTrigger
            size="sm"
            className={`h-8 w-36 ${tipoBonificacion !== 'ninguna' ? 'bg-emerald-50 text-emerald-700' : ''}`}
            aria-label={`Bonificación de ${item.nombre}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ninguna">Sin bonificación</SelectItem>
            <SelectItem value="porcentaje">Bonif. %</SelectItem>
            <SelectItem value="monto">Bonif. monto</SelectItem>
          </SelectContent>
        </Select>

        {tipoBonificacion !== 'ninguna' && (
          <>
            <NumberInput
              key={`bonificacion-${tipoBonificacion}-${item.bonificacionValor}`}
              aria-label={`Valor de la bonificación de ${item.nombre}`}
              currency={tipoBonificacion === 'monto'}
              decimals={tipoBonificacion === 'porcentaje' ? 4 : 2}
              min={0}
              max={tipoBonificacion === 'porcentaje' ? 100 : undefined}
              defaultValue={item.bonificacionValor ?? undefined}
              onAccept={(valor) => guardarBonificacion(tipoBonificacion, valor)}
              containerClassName={tipoBonificacion === 'porcentaje' ? 'w-24' : 'w-36'}
              className="h-8"
              placeholder={tipoBonificacion === 'porcentaje' ? 'Ej: 20' : '0.00'}
            />
            {tipoBonificacion === 'porcentaje' && <span className="text-muted-foreground">%</span>}
          </>
        )}
      </div>
    </div>
  );
};
